/**
 * Session lifetimes by audience (ADR-015 §6). The audience comes from the host, never from the body.
 *
 * | Audience | Access | Idle | Max lifetime | Cookie           |
 * |----------|--------|------|--------------|------------------|
 * | admin    | 10 min | 24 h | 7 d          | desp_rt_admin    |
 * | driver   | 10 min | 30 d | 90 d         | desp_rt_driver   |
 * | platform | 10 min | 1 h  | 12 h         | desp_rt_platform |
 */
export const SESSION_AUDIENCES = ['admin', 'driver', 'platform'] as const;
export type SessionAudience = (typeof SESSION_AUDIENCES)[number];
/** Audiences of a tenant member (`sessions.audience`); platform sessions live in `platform_sessions`. */
export type TenantAudience = Exclude<SessionAudience, 'platform'>;

export interface SessionPolicy {
  /** Lifetime of the access token (JWT). */
  accessTtlMs: number;
  /** A session not refreshed for this long expires (`idle_expires_at`). */
  idleTtlMs: number;
  /** Hard limit of the refresh family since sign-in (`family_expires_at`). */
  maxLifetimeMs: number;
  /** Refresh cookie; prefixed with `__Secure-` in production. */
  cookie: string;
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

export const SESSION_POLICIES: Readonly<Record<SessionAudience, SessionPolicy>> = {
  admin: { accessTtlMs: 10 * MINUTE, idleTtlMs: 24 * HOUR, maxLifetimeMs: 7 * DAY, cookie: 'desp_rt_admin' },
  driver: { accessTtlMs: 10 * MINUTE, idleTtlMs: 30 * DAY, maxLifetimeMs: 90 * DAY, cookie: 'desp_rt_driver' },
  platform: { accessTtlMs: 10 * MINUTE, idleTtlMs: 1 * HOUR, maxLifetimeMs: 12 * HOUR, cookie: 'desp_rt_platform' },
};

/**
 * After a rotation the previous refresh hash is accepted once, for this long, from the same device
 * and audience, and only to issue an access token. Anything else is reuse and revokes the family.
 */
export const REFRESH_GRACE_MS = 10_000;

/** Expiry instants of a session created or refreshed at `now` in a family started at `familyStartedAt`. */
export function sessionExpiry(audience: SessionAudience, familyStartedAt: Date, now: Date): { idleExpiresAt: Date; familyExpiresAt: Date } {
  const p = SESSION_POLICIES[audience];
  const familyExpiresAt = new Date(familyStartedAt.getTime() + p.maxLifetimeMs);
  const idle = Math.min(now.getTime() + p.idleTtlMs, familyExpiresAt.getTime());
  return { idleExpiresAt: new Date(idle), familyExpiresAt };
}
